import React, { useState, useEffect } from "react";
import {
  Box, Button, Typography, Dialog, DialogActions, DialogContent, DialogTitle,
  useMediaQuery, useTheme, Alert
} from "@mui/material";
import { useCategories } from "../../hooks/useCategories";
import CategoryTable from "../../components/CategoryTable";
import AddCategoryForm from "../../components/AddCategoryForm";
import { usePageContext } from "../../context/PageContext";
import { useTranslation } from "react-i18next";

const Categories = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    createMutation,
    updateMutation,
    deleteMutation,
    createSubcategory,
    updateSubcategory,
    deleteSubcategory,
    isLoading,
  } = useCategories({ page: currentPage, limit: pageSize, search });

  useEffect(() => {
    if (error) setErrorMessage(t("categoriesPage.loadError"));
    else setErrorMessage(null);
  }, [error, t]);

  // CRUD direto da tabela
  const upsertSubcategory = ({ parentId, sub }) => {
    setIsProcessing(true);
    const action = sub.id
      ? updateSubcategory(sub.id, sub)
      : createSubcategory(parentId, sub);

    action
      .then(() => refetch())
      .catch((err) =>
        setErrorMessage(err?.response?.data?.message || t("categoriesPage.saveSubError"))
      )
      .finally(() => setIsProcessing(false));
  };

  const removeSubcategory = ({ parentId, subId }) => {
    setIsProcessing(true);
    deleteSubcategory(subId)
      .then(() => refetch())
      .catch((err) =>
        setErrorMessage(err?.response?.data?.message || t("categoriesPage.deleteSubError"))
      )
      .finally(() => setIsProcessing(false));
  };

  const toggleCategoryStatus = (cat) => {
    setIsProcessing(true);
    updateMutation
      .mutateAsync({ id: cat.id, status: cat.status ? 0 : 1 })
      .then(() => refetch())
      .catch((err) =>
        setErrorMessage(err?.response?.data?.message || t("categoriesPage.toggleCategoryError"))
      )
      .finally(() => setIsProcessing(false));
  };

  const toggleSubStatus = (parentId, sub) => {
    setIsProcessing(true);
    updateSubcategory(sub.id, { ...sub, status: sub.status ? 0 : 1 })
      .then(() => refetch())
      .catch((err) =>
        setErrorMessage(err?.response?.data?.message || t("categoriesPage.toggleSubError"))
      )
      .finally(() => setIsProcessing(false));
  };

  // Salvar categoria + subcategorias vindas do form
  const saveCategoryWithChildren = async (formPayload) => {
    setIsProcessing(true);
    setErrorMessage(null);

    const { children = [], ...category } = formPayload || {};
    // console.log("[Categories] saveCategoryWithChildren payload:", formPayload);

    try {
      let categoryId = category?.id;

      if (categoryId) {
        await updateMutation.mutateAsync(category);
      } else {
        const res = await createMutation.mutateAsync(category);
        // fetchData() retorna { data: ... }
        categoryId = res?.data?.id ?? res?.id ?? res?.data?.insertId ?? res?.insertId;
        if (!categoryId) throw new Error("Não foi possível obter o ID da categoria criada.");
      }

      // Snapshot dos filhos que o usuário abriu para editar
      const originalChildren = selectedCategory?.children || [];
      const originalById = new Map(
        originalChildren.filter((c) => c?.id != null).map((c) => [String(c.id), c])
      );

      // >>> Regra robusta:
      // - criar: qualquer filho que NÃO exista no snapshot original
      // - atualizar: o que existe no snapshot original
      // - excluir: do snapshot original que sumiu do payload
      const toCreate = children.filter((c) => !c.id || !originalById.has(String(c.id)));
      const toUpdate = children.filter((c) => c.id && originalById.has(String(c.id)));
      const toDelete = originalChildren.filter(
        (oc) => !children.some((nc) => String(nc.id) === String(oc.id))
      );

      // console.log("[Categories] toCreate:", toCreate);
      // console.log("[Categories] toUpdate:", toUpdate);
      // console.log("[Categories] toDelete:", toDelete);

      // Dispara as operações
      await Promise.all([
        ...toCreate.map((s) => createSubcategory(categoryId, s)),
        ...toUpdate.map((s) => updateSubcategory(s.id, s)),
        ...toDelete.map((s) => deleteSubcategory(s.id)),
      ]);

      await refetch();
      handleCloseForm();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || t("categoriesPage.saveError"));
    } finally {
      setIsProcessing(false);
    }
  };

  // UI
  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setErrorMessage(null);
  };

  const handleDeleteCategory = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteCategory = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation
      .mutateAsync(confirmDelete)
      .then(() => {
        setConfirmDelete(null);
        refetch();
      })
      .catch((err) => {
        setErrorMessage(err?.response?.data?.message || t("categoriesPage.deleteOneError"));
      })
      .finally(() => setIsProcessing(false));
  };

  const handleSearch = (term) => {
    setSearch(term);
    refetch();
  };
  const handleCloseForm = () => {
    setSelectedCategory(null);
    setErrorMessage(null);
  };

  // ações em massa
  const bulkActivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 1 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || t("categoriesPage.bulkActivateError"));
    } finally {
      setIsProcessing(false);
    }
  };

  const bulkDeactivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 0 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || t("categoriesPage.bulkDeactivateError"));
    } finally {
      setIsProcessing(false);
    }
  };

  const bulkDelete = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => deleteMutation.mutateAsync(id)));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || t("categoriesPage.bulkDeleteError"));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex", flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center",
          mb: 2, gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>{t("categoriesPage.title")}</Typography>
        <Button
          variant="contained" color="primary" fullWidth={isMobile}
          onClick={() => setSelectedCategory({})}
        >
          {t("categoriesPage.addCategory")}
        </Button>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedCategory ? (
        <AddCategoryForm
          categoryData={selectedCategory}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSubmit={saveCategoryWithChildren}   // <<<<<< ESSENCIAL
        />
      ) : (
        <CategoryTable
          categories={data?.tree || []}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={(p) => setCurrentPage(p + 1)}
          onPageSizeChange={setPageSize}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onEditCategory={handleEditCategory}
          onDeleteCategory={handleDeleteCategory}
          onUpsertSubcategory={upsertSubcategory}
          onDeleteSubcategory={removeSubcategory}
          onToggleCategoryStatus={toggleCategoryStatus}
          onToggleSubStatus={toggleSubStatus}
          onBulkActivate={bulkActivate}
          onBulkDeactivate={bulkDeactivate}
          onBulkDelete={bulkDelete}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t("shared.deleteConfirmation")}</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? t("shared.processingDelete") : t("categoriesPage.deleteQuestion")}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>{t("shared.cancel")}</Button>
              <Button color="error" onClick={confirmDeleteCategory}>{t("shared.delete")}</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories;
